import React from 'react';
import { Helmet } from 'react-helmet';

const StatisticsCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-pink-100 to-red-100">
      <Helmet>
        <title>Statistics Calculators | Comprehensive Statistical Tools</title>
        <meta name="description" content="Collection of 175+ statistics calculators for data analysis, probability, hypothesis testing, regression analysis, and descriptive statistics." />
        <meta name="keywords" content="statistics calculators, data analysis tools, probability calculators, hypothesis testing calculators, regression analysis calculators" />
        <meta property="og:title" content="Statistics Calculators | Comprehensive Statistical Tools" />
        <meta property="og:description" content="Collection of 175+ statistics calculators for data analysis, probability, hypothesis testing, regression analysis, and descriptive statistics." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Statistics Calculators | Comprehensive Statistical Tools" />
        <meta name="twitter:description" content="Collection of 175+ statistics calculators for data analysis, probability, hypothesis testing, regression analysis, and descriptive statistics." />
        <link rel="canonical" href="https://yourdomain.com/statistics-calculators" />
      </Helmet>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-6 right-6">
        <a 
          href="https://api.whatsapp.com/send?phone=YOUR_PHONE_NUMBER&text=I%20need%20help%20with%20the%20Statistics%20Calculators" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-full shadow-lg transition-all transform hover:scale-105"
        >
          <span className="text-2xl">💬</span>
          <span className="sr-only">Contact us on WhatsApp</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-red-600 mb-4">📊</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Statistics Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">175+ calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Explore our comprehensive collection of statistics calculators designed to help with data analysis, probability calculations, hypothesis testing, regression analysis, and descriptive statistics. These tools are invaluable for students, researchers, and professionals working with data.
            </p>
            <button className="read-more inline-block bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Descriptive Statistics <span className="emoji ml-2">📋</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mean-median-mode" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mean, Median, Mode Calculator</h3>
                <p className="text-gray-600">Calculate central tendency measures</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/standard-deviation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Standard Deviation Calculator</h3>
                <p className="text-gray-600">Measure data dispersion</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/variance" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Variance Calculator</h3>
                <p className="text-gray-600">Calculate data variability</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/quartiles" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Quartiles Calculator</h3>
                <p className="text-gray-600">Determine data distribution quartiles</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/interquartile-range" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Interquartile Range Calculator</h3>
                <p className="text-gray-600">Calculate middle 50% of data</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/z-score" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Z-Score Calculator</h3>
                <p className="text-gray-600">Standardize data points</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Probability Distributions <span className="emoji ml-2">🎲</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/normal-distribution" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Normal Distribution Calculator</h3>
                <p className="text-gray-600">Calculate probabilities for normal distribution</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/binomial-distribution" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Binomial Distribution Calculator</h3>
                <p className="text-gray-600">Calculate binomial probabilities</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/poisson-distribution" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Poisson Distribution Calculator</h3>
                <p className="text-gray-600">Calculate Poisson probabilities</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/t-distribution" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">t-Distribution Calculator</h3>
                <p className="text-gray-600">Calculate Student's t-distribution probabilities</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/chi-square-distribution" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Chi-Square Distribution Calculator</h3>
                <p className="text-gray-600">Calculate chi-square distribution probabilities</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/f-distribution" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">F-Distribution Calculator</h3>
                <p className="text-gray-600">Calculate F-distribution probabilities</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Hypothesis Testing <span className="emoji ml-2">🔍</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/t-test" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">t-Test Calculator</h3>
                <p className="text-gray-600">Compare means of two groups</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/z-test" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">z-Test Calculator</h3>
                <p className="text-gray-600">Test hypotheses about population proportions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/chi-square-test" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Chi-Square Test Calculator</h3>
                <p className="text-gray-600">Test independence and goodness of fit</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/anova" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">ANOVA Calculator</h3>
                <p className="text-gray-600">Compare means of three or more groups</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mann-whitney-test" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mann-Whitney U Test Calculator</h3>
                <p className="text-gray-600">Compare two independent samples (non-parametric)</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/wilcoxon-test" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Wilcoxon Signed-Rank Test Calculator</h3>
                <p className="text-gray-600">Compare two related samples (non-parametric)</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Regression Analysis <span className="emoji ml-2">📈</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/linear-regression" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Linear Regression Calculator</h3>
                <p className="text-gray-600">Model linear relationships between variables</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/multiple-regression" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Multiple Regression Calculator</h3>
                <p className="text-gray-600">Model relationships with multiple predictors</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/logistic-regression" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Logistic Regression Calculator</h3>
                <p className="text-gray-600">Model binary outcome probabilities</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/correlation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Correlation Calculator</h3>
                <p className="text-gray-600">Measure relationship strength between variables</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/coefficient-of-determination" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Coefficient of Determination Calculator</h3>
                <p className="text-gray-600">Calculate R-squared values</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/regression-diagnostics" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Regression Diagnostics Calculator</h3>
                <p className="text-gray-600">Assess regression model assumptions</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Data Visualization <span className="emoji ml-2">📊</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/histogram" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Histogram Generator</h3>
                <p className="text-gray-600">Create frequency distribution charts</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/box-plot" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Box Plot Generator</h3>
                <p className="text-gray-600">Visualize data spread and outliers</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/scatter-plot" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Scatter Plot Generator</h3>
                <p className="text-gray-600">Visualize relationships between variables</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/bar-chart" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Bar Chart Generator</h3>
                <p className="text-gray-600">Compare categorical data</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/pie-chart" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pie Chart Generator</h3>
                <p className="text-gray-600">Display proportional relationships</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/statistical-map" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Statistical Map Generator</h3>
                <p className="text-gray-600">Visualize geographical data distributions</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsCalculators;